import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { useChatRefs } from '@/contexts/ChatRefs/useChatRefs'

const BOTTOM_THRESHOLD = 100 // px from the bottom regarded as “near bottom”
const SPACER_HEIGHT = 64 // px

/**
 * Handles the scrolling of the chat messages.
 *
 * @param {Array} messages  The array of messages to scroll
 * @returns {Object}  showScrollButton  boolean to show the scroll button
 * @returns {Object}  scrollToBottom  function to scroll to the bottom
 * @returns {Object}  atBottomRef  boolean to check if the user is at the bottom
 * @returns {Object}  autoScrollEnabledRef  boolean to check if auto scroll is enabled
 */
export default function useChatScroll(messages, followUpsEnabled = false) {
  const { messagesContainerRef } = useChatRefs()

  const atBottomRef = useRef(true) // mutable flag, no re-renders
  const autoScrollEnabledRef = useRef(true)
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const [showSuggestedFollowUpPrompts, setShowSuggestedFollowUpPrompts] =
    useState(false)

  /* ------------------------------------------------------------------ *
   * 1. Scroll listener – updates `atBottom` & button visibility
   * ------------------------------------------------------------------ */
  useEffect(() => {
    const el = messagesContainerRef.current
    if (!el) return

    const onScroll = () => {
      const realScrollHeight = el.scrollHeight
      const distanceFromBottom =
        realScrollHeight - el.scrollTop - el.clientHeight
      const nearBottom = distanceFromBottom < BOTTOM_THRESHOLD

      atBottomRef.current = nearBottom
      if (followUpsEnabled) {
        setShowScrollBtn(distanceFromBottom > 1000)
        setShowSuggestedFollowUpPrompts(distanceFromBottom < 250)
      } else {
        setShowScrollBtn(!nearBottom)
      }

      // **if they’re near bottom**, re-enable auto-scroll
      if (nearBottom) autoScrollEnabledRef.current = true
      // **if they scroll up**, disable auto-scroll
      else autoScrollEnabledRef.current = false
    }

    // attach listener
    el.addEventListener('scroll', onScroll, { passive: true })
    // ** initial sync **
    onScroll()
    return () => el.removeEventListener('scroll', onScroll)
  }, [messagesContainerRef])

  /* ------------------------------------------------------------------ *
   * 2. Auto-scroll on new content – only if user was near bottom
   * ------------------------------------------------------------------ */
  useLayoutEffect(() => {
    if (messages.length === 0) return
    if (!atBottomRef.current) return
    const el = messagesContainerRef.current
    if (el) el.scrollTop = el.scrollHeight // instant; smooth is janky
  }, [messages, messagesContainerRef])

  /* ------------------------------------------------------------------ *
   * 3. Exposed helpers
   * ------------------------------------------------------------------ */
  const scrollToBottom = useCallback(() => {
    const el = messagesContainerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messagesContainerRef])

  return {
    showScrollButton: showScrollBtn,
    scrollToBottom,
    showSuggestedFollowUpPrompts,
    atBottomRef,
    autoScrollEnabledRef,
  }
}
